import type { FC, CSSProperties } from 'react';
import { Button, Flex, Tag, Typography } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  WORK_ORDER_UI,
  softBadgeStyle,
} from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import type { OptimizerClusterView } from '../../types';
import { statusTone } from '../../utils';

export interface OptimizerClusterCardProps {
  cluster: OptimizerClusterView;
  canManage: boolean;
  onApply: () => void;
  onSelectWorkOrder: (workOrderId: string) => void;
}

/** Fixed footprint — same size + Apply on the same baseline in every card. */
export const OPTIMIZER_CLUSTER_CARD_WIDTH = 280;
export const OPTIMIZER_CLUSTER_CARD_HEIGHT = 320;

const shellStyle: CSSProperties = {
  boxSizing: 'border-box',
  width: OPTIMIZER_CLUSTER_CARD_WIDTH,
  minWidth: OPTIMIZER_CLUSTER_CARD_WIDTH,
  maxWidth: OPTIMIZER_CLUSTER_CARD_WIDTH,
  height: OPTIMIZER_CLUSTER_CARD_HEIGHT,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 12,
  borderRadius: 12,
  border: `1px solid ${WORK_ORDER_UI.cardBorder}`,
  background: '#fff',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

export const OptimizerClusterCard: FC<OptimizerClusterCardProps> = ({
  cluster,
  canManage,
  onApply,
  onSelectWorkOrder,
}) => {
  const { t } = useTranslation('maintenance');
  const codes = cluster.workOrders.map((wo) => wo.code).join(', ');

  return (
    <div style={shellStyle}>
      <Flex
        justify="space-between"
        align="start"
        gap={8}
        style={{ flexShrink: 0 }}
      >
        <div style={{ minWidth: 0 }}>
          <Typography.Text strong style={{ display: 'block' }}>
            {t('workOrders.kanban.optimizer.clusterTitle', {
              week: cluster.isoWeekKey,
            })}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {cluster.siteName}
          </Typography.Text>
        </div>
        <Tag style={softBadgeStyle(WORK_ORDER_UI.status.PLANNED)}>
          {t('workOrders.kanban.optimizer.woCount', {
            count: cluster.workOrders.length,
          })}
        </Tag>
      </Flex>

      <Typography.Text
        type="secondary"
        style={{
          flexShrink: 0,
          display: 'block',
          fontSize: 12,
          lineHeight: 1.45,
          background: '#f8fafc',
          borderRadius: 8,
          border: `1px solid ${WORK_ORDER_UI.cardBorder}`,
          padding: '6px 8px',
        }}
      >
        {t('workOrders.kanban.optimizer.reasonNote', {
          site: cluster.siteName,
          week: cluster.isoWeekKey,
        })}
      </Typography.Text>

      {/* Grows to push footer down; scrolls when many WOs */}
      <div
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {cluster.workOrders.map((wo) => (
          <Flex key={wo.id} justify="space-between" align="center" gap={8}>
            <Typography.Link
              onClick={() => onSelectWorkOrder(wo.id)}
              style={{ fontWeight: 600 }}
            >
              {wo.code}
            </Typography.Link>
            <Tag style={softBadgeStyle(statusTone(wo.status))}>
              {t(`workOrders.status.${wo.status}`, {
                defaultValue: wo.status,
              })}
            </Tag>
          </Flex>
        ))}
      </div>

      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <Typography.Text
          style={{
            fontSize: 12,
            color: WORK_ORDER_UI.suggestionLink,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <LinkOutlined />
          {t('workOrders.kanban.optimizer.canBundle', { codes })}
        </Typography.Text>

        {canManage ? (
          <Button
            type="primary"
            size="small"
            block
            onClick={onApply}
            style={{
              background: WORK_ORDER_UI.primary,
              borderColor: WORK_ORDER_UI.primary,
            }}
          >
            {t('workOrders.kanban.optimizer.apply')}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
